/**
 * Shared subscription copy helpers.
 *
 * Kept in one place so the modal (PaywallFlow) and the subscription page
 * can never drift apart again: both render the same user label and the
 * same first-invoice promise.
 */
import { formatISK } from '@/utils/formatISK';

const IS_MONTHS = ['janúar', 'febrúar', 'mars', 'apríl', 'maí', 'júní',
  'júlí', 'ágúst', 'september', 'október', 'nóvember', 'desember'];
const EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * dd.mm.yyyy — the single date format used across the Icelandic UI.
 *
 * iter368: the server sends a plain YYYY-MM-DD (Atlantic/Reykjavik) and we
 * format the STRING, never a Date. Parsing it into an instant used to move
 * the first-invoice date a day in any browser west of Reykjavik.
 */
export const formatDateIS = (input) => {
  if (!input) return '';
  if (input instanceof Date) {
    if (isNaN(input.getTime())) return '';
    const dd = String(input.getDate()).padStart(2, '0');
    const mm = String(input.getMonth() + 1).padStart(2, '0');
    return `${dd}.${mm}.${input.getFullYear()}`;
  }
  const [y, m, d] = String(input).slice(0, 10).split('-');
  if (!y || !m || !d) return String(input);
  return `${d}.${m}.${y}`;
};

/** "október 2026" in Icelandic, "October 2026" in English. */
export const formatMonthYear = (input, lang) => {
  if (!input) return '';
  const [y, m] = input instanceof Date
    ? [String(input.getFullYear()), String(input.getMonth() + 1).padStart(2, '0')]
    : String(input).slice(0, 10).split('-');
  const idx = Number(m) - 1;
  if (!y || isNaN(idx) || idx < 0 || idx > 11) return String(input);
  if (lang === 'is') return `${IS_MONTHS[idx]} ${y}`;
  return `${EN_MONTHS[idx]} ${y}`;
};

/** "3. nóvember 2026" / "3 November 2026" from a plain YYYY-MM-DD. */
export const formatLongDate = (input, lang) => {
  if (!input) return '';
  const [y, m, d] = String(input).slice(0, 10).split('-');
  const idx = Number(m) - 1;
  if (!y || !d || isNaN(idx) || idx < 0 || idx > 11) return String(input);
  return lang === 'is'
    ? `${Number(d)}. ${IS_MONTHS[idx]} ${y}`
    : `${EN_MONTHS[idx]} ${Number(d)}, ${y}`;
};

/** "1 notandi" never "Allt að 1 notendur". */
export const usersLabel = (n, lang) => {
  const count = Number(n) || 0;
  if (lang === 'is') {
    return count === 1 ? '1 notandi' : `Allt að ${count} notendur`;
  }
  return count === 1 ? '1 user' : `Up to ${count} users`;
};

/**
 * One short line under each pakki name (iter366).
 *
 * Only what the code actually enforces: the user limit (check_user_limit in
 * server.py) is the only hard difference between team and business, and the
 * extra sentences say what that limit means in practice. No tooltips — they
 * do not work on phones.
 */
const PLAN_TAGLINES = {
  is: {
    solo: '1 notandi. Fyrir þig einan.',
    team: 'Allt að 5 notendur. Starfsmenn skrá tíma sjálfir.',
    business: 'Allt að 15 notendur.',
  },
  en: {
    solo: '1 user. Just you.',
    team: 'Up to 5 users. Staff log their own hours.',
    business: 'Up to 15 users.',
  },
};

export const planTagline = (plan, maxUsers, lang) => {
  const table = PLAN_TAGLINES[lang === 'is' ? 'is' : 'en'];
  return table[plan] || `${usersLabel(maxUsers, lang)}.`;
};

/**
 * When the customer gets billed the first time. A company still inside its
 * 14 day trial is NOT invoiced on plan selection, so promising an immediate
 * invoice was simply wrong.
 */
export const firstInvoiceLine = (subscription, lang) => {
  // Accepts both subscription shapes: /billing/subscription (in_trial) and
  // /company/subscription (subscription_status + trial_days_remaining).
  const status = subscription?.status || subscription?.subscription_status;
  const daysLeft = subscription?.trial_days_remaining;
  const inTrial = subscription && 'in_trial' in subscription
    ? subscription.in_trial === true
    : (['trialing', 'trial'].includes(status) && (daysLeft == null || daysLeft > 0));
  const endsAt = subscription?.first_invoice_date || subscription?.trial_ends_at;
  if (inTrial && endsAt) {
    return lang === 'is'
      ? `Fyrsti reikningur berst þegar prufutímanum lýkur ${formatDateIS(endsAt)}.`
      : `Your first invoice arrives when the trial ends on ${formatDateIS(endsAt)}.`;
  }
  return lang === 'is'
    ? 'Fyrsti reikningur er sendur strax.'
    : 'Your first invoice is sent right away.';
};

/**
 * ONE number per invoice, on every screen, always labelled (iter373).
 *
 * Fjármál showed `subtotal` (án VSK) and the Reikningsfærslur tab showed
 * `total` (m. VSK) for the same invoice, with no label, so DRAFT-0004 read
 * 190.666 on one screen and 198.986 on the other. The customer-facing number
 * is the one with VAT; when the invoice carries no VAT at all it is labelled
 * án VSK instead of pretending.
 */
/**
 * iter373: the one screen a reopened company sees. Same promise as signup,
 * because these owners never saw "nema þú segir upp" the first time round.
 */
export const reopenNotice = (subscription, priceIsk, lang) => {
  const endsAt = subscription?.first_invoice_date || subscription?.trial_ends_at;
  if (!endsAt) return null;
  return lang === 'is'
    ? `14 dagar frítt. Fyrsti reikningur ${formatDateIS(endsAt)}, ${formatISK(priceIsk)} kr á mánuði, nema þú segir upp.`
    : `14 days free. First invoice ${formatDateIS(endsAt)}, ${formatISK(priceIsk)} kr per month, unless you cancel.`;
};

export const invoiceAmount = (inv, vatRegistered = null) => {
  const total = Number(inv?.total ?? 0);
  const subtotal = Number(inv?.subtotal ?? 0);
  const vat = Number(inv?.vat_total ?? (total - subtotal));
  // iter385 #3: ONE label and ONE basis for every row in a list. Deciding per
  // invoice made the Reikningsfærslur tab show "m. VSK" on an old 24 % invoice
  // and "án VSK" on the new ones for the same company, so two rows could not
  // be compared. The company's VAT registration decides the label; the amount
  // is always what the customer pays.
  if (vatRegistered !== null) {
    return {
      amount: total || subtotal,
      label: vatRegistered ? 'm. VSK' : 'án VSK',
      withVat: !!vatRegistered,
    };
  }
  if (vat > 0) return { amount: total, label: 'm. VSK', withVat: true };
  return { amount: total || subtotal, label: 'án VSK', withVat: false };
};
